import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, CircleCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState, SectionCard, formatCurrency, formatNumber } from '../../../components/design-system/admin';
import type { AttentionItem } from '../../../common/models/admin-overview.model';

const VISIBLE = 6;

const DOT: Record<AttentionItem['severity'], string> = {
  high: 'bg-destructive',
  medium: 'bg-warning',
  low: 'bg-muted-foreground/50',
};

/** Where each kind of item is dealt with. */
export function attentionHref(item: AttentionItem): string {
  switch (item.kind) {
    // Straight onto the jobs in question: stuck ones over the page's longest
    // range (the API counts back 30 days), failures over the week it counts.
    case 'jobs_stuck':
      return `/queue-monitor?queue=${item.meta?.queue === 'seamless' ? 'seamless' : 'bulk-products'}&hours=720&status=stuck`;
    case 'jobs_failed':
      return '/queue-monitor?hours=168&status=failed';
    case 'support_unread':
      return '/support';
    case 'quotes_new':
      return '/quote-requests';
    case 'invoices_overdue':
      return '/wire-invoices';
    default:
      return item.vendor ? `/vendors/${item.vendor.id}/edit` : '/vendors';
  }
}

/** The sentence for an item, and a second line with the detail. */
export function useAttentionText() {
  const { t } = useTranslation();
  return (item: AttentionItem): { title: string; detail?: string } => {
    const m = item.meta || {};
    const n = { count: item.count };
    switch (item.kind) {
      case 'jobs_stuck':
        return { title: t('ADMIN_OVERVIEW.ATTENTION.JOBS_STUCK', n), detail: t('ADMIN_OVERVIEW.ATTENTION.JOBS_STUCK_META', { days: m.oldestDays }) };
      case 'jobs_failed':
        return { title: t('ADMIN_OVERVIEW.ATTENTION.JOBS_FAILED', n), detail: item.vendor?.name };
      case 'studio_failed':
        return { title: t('ADMIN_OVERVIEW.ATTENTION.STUDIO_FAILED', n), detail: item.vendor?.name };
      case 'quota_near':
      case 'quota_over':
        return {
          title: `${item.vendor?.name} · ${t(item.kind === 'quota_over' ? 'ADMIN_OVERVIEW.ATTENTION.QUOTA_OVER' : 'ADMIN_OVERVIEW.ATTENTION.QUOTA_NEAR', { percent: m.percent })}`,
          detail: t('ADMIN_OVERVIEW.ATTENTION.QUOTA_META', { used: formatNumber(item.count), limit: formatNumber(Number(m.limit)) }),
        };
      case 'vendor_quiet':
        return { title: `${item.vendor?.name} · ${t('ADMIN_OVERVIEW.ATTENTION.VENDOR_QUIET', { days: m.days })}` };
      case 'vendor_not_live':
        return { title: `${item.vendor?.name} · ${t('ADMIN_OVERVIEW.ATTENTION.VENDOR_NOT_LIVE', { days: m.days })}` };
      case 'vendor_no_products':
        return { title: `${item.vendor?.name} · ${t('ADMIN_OVERVIEW.ATTENTION.VENDOR_NO_PRODUCTS')}` };
      case 'support_unread':
        return { title: t('ADMIN_OVERVIEW.ATTENTION.SUPPORT_UNREAD', n) };
      case 'quotes_new':
        return { title: t('ADMIN_OVERVIEW.ATTENTION.QUOTES_NEW', n) };
      case 'invoices_overdue':
        return {
          title: t('ADMIN_OVERVIEW.ATTENTION.INVOICES_OVERDUE', n),
          detail: t('ADMIN_OVERVIEW.ATTENTION.INVOICES_OVERDUE_META', { amount: formatCurrency(Number(m.amount), String(m.currency || 'usd'), 0) }),
        };
      default:
        return { title: item.kind };
    }
  };
}

/** What a person should look at, most urgent first: each row says what and links to where it is dealt with. */
export function AttentionList({ items, className }: { items: AttentionItem[]; className?: string }) {
  const { t } = useTranslation();
  const text = useAttentionText();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, VISIBLE);

  return (
    <SectionCard
      title={t('ADMIN_OVERVIEW.ATTENTION.TITLE')}
      description={t('ADMIN_OVERVIEW.ATTENTION.DESCRIPTION')}
      className={className}
      flush
      testId="overview-attention"
    >
      {items.length === 0 ? (
        <EmptyState icon={<CircleCheck />} title={t('ADMIN_OVERVIEW.ATTENTION.ALL_CLEAR')} description={t('ADMIN_OVERVIEW.ATTENTION.ALL_CLEAR_BODY')} />
      ) : (
        <>
          <ul className="divide-y border-t">
            {shown.map((item, i) => {
              const { title, detail } = text(item);
              return (
                <li key={`${item.kind}-${item.vendor?.id ?? i}`}>
                  <Link
                    to={attentionHref(item)}
                    className="group flex items-start gap-3 px-4 py-2.5 transition-colors hover:bg-accent/40"
                    data-testid="attention-item"
                    data-kind={item.kind}
                  >
                    <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', DOT[item.severity])} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{title}</span>
                      {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
                    </span>
                    <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 rtl:rotate-180" />
                  </Link>
                </li>
              );
            })}
          </ul>
          {items.length > VISIBLE && (
            <div className="border-t px-2 py-1.5">
              <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setExpanded((e) => !e)}>
                {expanded ? t('ADMIN_OVERVIEW.ATTENTION.SHOW_LESS') : t('ADMIN_OVERVIEW.ATTENTION.SHOW_MORE', { count: items.length - VISIBLE })}
              </Button>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}
